/**
 * Font catalog — the union of every font that any client can render.
 *
 * Source of truth: `catalog.json` in this folder. Single file consumed by:
 *  - apps/app  — generates @fontsource imports + drives the preload pipeline
 *  - apps/web  — builds the Google Fonts <link> URL for lazy loading
 *  - apps/swift — codegen reads it to produce `FontCatalog.swift`
 *
 * Each entry carries enough metadata that all three clients can render the
 * same font without duplicating per-platform tables.
 */

import catalog from "./catalog.json";

export type FontCategory = "sans" | "serif" | "mono";

export interface FontCatalogEntry {
  id: string;
  family: string;
  category: FontCategory;
  /**
   * True when the font is resolved from the host OS rather than bundled.
   * Skip @fontsource imports, Google Fonts loading, and Swift TTF bundling
   * for these entries — each client falls back to the platform's system font.
   */
  system?: boolean;
  fontsourcePackage: string;
  fontsourceVariant: "variable" | "static";
  /** Italic CSS file for variable @fontsource packages that ship italics separately. */
  fontsourceItalic?: string;
  /** Static weights to import when `fontsourceVariant === "static"`. */
  fontsourceStaticWeights?: string[];
  googleFontsParam: string;
  swiftBundleFiles: string[];
}

const entries = catalog.fonts as FontCatalogEntry[];

export const fontCatalog: readonly FontCatalogEntry[] = entries;

export const fontsById: Readonly<Record<string, FontCatalogEntry>> = Object.freeze(
  Object.fromEntries(entries.map((f) => [f.id, f])),
);

export function fontsByCategory(category: FontCategory): FontCatalogEntry[] {
  return entries.filter((f) => f.category === category);
}

/**
 * CSS `font-family` stack value. Variable @fontsource packages register a
 * "Family Variable" name, so we list both — and end with a category fallback.
 */
const CATEGORY_FALLBACK: Record<FontCategory, string> = {
  sans: 'system-ui, -apple-system, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: '"Fira Code", "SF Mono", monospace',
};

export function fontFamilyValue(entry: FontCatalogEntry): string {
  if (entry.system) {
    return CATEGORY_FALLBACK[entry.category];
  }
  if (entry.fontsourceVariant === "variable") {
    return `"${entry.family} Variable", "${entry.family}", ${CATEGORY_FALLBACK[entry.category]}`;
  }
  return `"${entry.family}", ${CATEGORY_FALLBACK[entry.category]}`;
}

/** Family value for a font id; falls back to the sans default if missing. */
export function fontFamilyValueById(id: string): string {
  const entry = fontsById[id];
  if (entry) return fontFamilyValue(entry);
  return CATEGORY_FALLBACK.sans;
}
